"use client";

import Link from "next/link";
import Image from "next/image";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import type { ProductItemProps } from "src/lib/components/product-item/default/common";
import { formatMoney, toAssetImages } from "src/lib/utils";

import { SearchResultCard } from "./search-result-card";

export type SearchResultProps = {
  results: ProductItemProps[];
  isLoading?: boolean;
};

function formatProductPrice(salePrice: ProductItemProps["salePrice"]) {
  if (salePrice === undefined || salePrice === null) return "—";
  const n = typeof salePrice === "string" ? parseInt(salePrice, 10) : salePrice;
  if (Number.isNaN(n)) return "—";
  return formatMoney(n);
}

function stockLabel(item: ProductItemProps) {
  const n = item.totalAvailableQuantity ?? item.totalStockQuantity ?? 0;
  if (n <= 0) return "Hết hàng";
  return `Tồn: ${n}`;
}

const SearchResult = ({ results, isLoading }: SearchResultProps) => (
  <SearchResultCard elevation={2}>
    {isLoading ? (
      <LinearProgress
        color="primary"
        sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1, borderRadius: 1 }}
      />
    ) : null}
    {isLoading && results.length === 0 ? (
      <MenuItem disabled sx={{ py: 2, justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Đang tìm…
        </Typography>
      </MenuItem>
    ) : null}
    {results.map((item) => (
      <Link
        key={item.code}
        href={`/product/${item.slugName}`}
        style={{ textDecoration: "none", color: "inherit" }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <MenuItem
          sx={{
            alignItems: "flex-start",
            gap: 1.5,
            py: 1.25,
            px: { xs: 1.5, sm: 2 },
            minHeight: 64,
            whiteSpace: "normal",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {item.imageUrl ? (
              <Image
                src={toAssetImages(item.imageUrl)}
                alt={item.name}
                width={48}
                height={48}
                style={{ objectFit: "contain" }}
              />
            ) : (
              <Typography variant="caption" color="text.disabled">
                —
              </Typography>
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ lineHeight: 1.3 }}
              noWrap
              title={item.name}
            >
              {item.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              SKU: {item.code}
              {item.barcode ? ` · ${item.barcode}` : ""}
            </Typography>
            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "baseline", mt: 0.25 }}
            >
              <Typography variant="body2" color="primary.main" fontWeight={600} component="span">
                {formatProductPrice(item.salePrice)}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="span">
                {stockLabel(item)}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      </Link>
    ))}
  </SearchResultCard>
);

export default SearchResult;
